import React, { useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { X, Smartphone, Wallet, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { triggerHapticFeedback } from '../../utils/capacitorHelper';
import { createDeposit, pollDeposit } from '../../services/pawapayService';
import { useCountry } from '../../contexts/CountryContext';

/**
 * Payment Modal - Shows after trip completion
 * Payment options: Airtel Money, TNM Mpamba, Cash
 */
const PaymentModal = ({ 
  isOpen, 
  onClose, 
  rideDetails,
  finalFare,
  onPaymentComplete
}) => {
  const ctx = useCountry();
  const [selectedMethod, setSelectedMethod] = useState('cash');
  const [phoneNumber, setPhoneNumber] = useState(() => {
    try { return localStorage.getItem('yachangu_last_momo') || ''; } catch { return ''; }
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [statusText, setStatusText] = useState('');
  const [payFailed, setPayFailed] = useState(false);
  const [failMessage, setFailMessage] = useState('');

  if (!isOpen) return null;

  // Amount charged in the user's local currency (fares are computed in MWK base)
  const localAmount = ctx ? ctx.convertFromBase(finalFare) : Number(finalFare || 0);
  const fareLabel = ctx ? ctx.formatMoney(finalFare) : `MWK ${finalFare?.toLocaleString()}`;

  // Mobile-money options come from the detected country (cash always available)
  const momoMethods = (ctx?.providers || []).map((p) => ({
    id: p.id,
    name: p.label,
    icon: <Smartphone className="w-6 h-6" />,
    color: 'border-emerald-500 bg-emerald-50',
    requiresPhone: true,
    provider: p.id,
  }));
  const paymentMethods = [
    ...momoMethods,
    {
      id: 'cash',
      name: 'Cash',
      icon: <Wallet className="w-6 h-6" />,
      color: 'border-green-500 bg-green-50',
      requiresPhone: false
    }
  ];
  const isMomo = (m) => m !== 'cash';

  const handlePayment = async () => {
    const selectedMethodData = paymentMethods.find(m => m.id === selectedMethod);

    // Validate phone number for mobile money
    if (selectedMethodData?.requiresPhone) {
      if (!phoneNumber || phoneNumber.length < 9) {
        toast({
          title: 'Invalid Phone Number',
          description: 'Please enter a valid phone number',
          variant: 'destructive'
        });
        return;
      }
    }

    setIsProcessing(true);

    // Mobile-money (any provider from the detected country) → PawaPay collection
    if (isMomo(selectedMethod)) {
      setPayFailed(false);
      setFailMessage('');
      try { localStorage.setItem('yachangu_last_momo', phoneNumber); } catch (_) { /* ignore */ }
      try {
        setStatusText('Sending payment request to your phone…');
        const dep = await createDeposit({
          amount: localAmount,
          phoneNumber,
          provider: selectedMethod,
          country: ctx?.detectedIso3,
          rideId: rideDetails?.id,
          userId: rideDetails?.rider_id,
          description: 'Yachangu trip fare',
        });
        setStatusText('Approve the mobile-money prompt on your phone…');
        const finalStatus = await pollDeposit(dep.depositId, {
          onUpdate: (s) => {
            if (s === 'SUBMITTED' || s === 'ACCEPTED' || s === 'PENDING' || !s) {
              setStatusText('Waiting for you to approve on your phone…');
            }
          },
        });

        if (finalStatus === 'COMPLETED') {
          // Record the payment against the ride
          try {
            await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rides/${rideDetails.id}/payment`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                payment_method: selectedMethod,
                amount: finalFare,
                phone_number: phoneNumber,
                deposit_id: dep.depositId,
              }),
            });
          } catch (_) { /* ride record is best-effort; payment already collected */ }
          triggerHapticFeedback('medium');
          setPaymentSuccess(true);
          toast({ title: 'Payment Successful', description: 'Mobile money payment received' });
          setTimeout(() => onPaymentComplete({ success: true, deposit_id: dep.depositId }), 2000);
        } else if (finalStatus === 'FAILED') {
          setPayFailed(true);
          setFailMessage('The mobile-money payment did not go through.');
          toast({ title: 'Payment Failed', description: 'The payment did not go through. Try again or switch provider.', variant: 'destructive' });
        } else if (finalStatus === 'IN_RECONCILIATION') {
          toast({ title: 'Payment pending', description: 'Your payment is being confirmed. We\u2019ll update you shortly.' });
        } else {
          setPayFailed(true);
          setFailMessage('Payment is taking longer than usual.');
          toast({ title: 'Still processing', description: 'Payment is taking longer than usual. Try again or switch provider.', variant: 'destructive' });
        }
      } catch (err) {
        const msg = err?.detail?.message || err?.detail || err?.message || 'Could not start the mobile-money payment';
        setPayFailed(true);
        setFailMessage(typeof msg === 'string' ? msg : 'Could not start the payment.');
        toast({ title: 'Payment Error', description: typeof msg === 'string' ? msg : 'Please try again', variant: 'destructive' });
      } finally {
        setIsProcessing(false);
        setStatusText('');
      }
      return;
    }

    // Cash payment
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rides/${rideDetails.id}/payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payment_method: selectedMethod,
          amount: finalFare,
          phone_number: phoneNumber || null
        })
      });

      const data = await response.json();

      if (data.success) {
        setPaymentSuccess(true);
        triggerHapticFeedback('medium');
        toast({
          title: 'Payment Successful',
          description: 'Please pay the driver in cash'
        });
        setTimeout(() => {
          onPaymentComplete(data);
        }, 2000);
      } else {
        toast({
          title: 'Payment Failed',
          description: data.message || 'Please try again',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast({
        title: 'Payment Error',
        description: 'Failed to process payment',
        variant: 'destructive'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  if (paymentSuccess) {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center">
        <div className="absolute inset-0 bg-black bg-opacity-50" />
        <Card className="relative w-full max-w-md mx-4 p-8 text-center">
          <CheckCircle className="w-20 h-20 text-green-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Payment Complete!</h2>
          <p className="text-gray-600 mb-4">Thank you for riding with Ride Yachangu</p>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-sm text-gray-700">
              Amount Paid: <span className="font-bold text-green-700">{fareLabel}</span>
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50" />
      
      {/* Modal */}
      <Card className="relative w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-bold text-gray-800">Complete Payment</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {/* Trip Summary */}
          <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-lg p-4 mb-6">
            <h3 className="text-lg font-bold text-gray-800 mb-3">Trip Summary</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-700">Base Fare:</span>
                <span className="font-semibold">MWK {rideDetails?.estimated_price?.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-700">Distance:</span>
                <span className="font-semibold">{rideDetails?.distance?.toFixed(1)} km</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-700">Duration:</span>
                <span className="font-semibold">{Math.round(rideDetails?.eta || 0)} min</span>
              </div>
              <div className="border-t border-green-300 pt-2 mt-2 flex justify-between">
                <span className="text-lg font-bold text-gray-800">Total Amount:</span>
                <span className="text-xl font-bold text-green-700">
                  {fareLabel}
                </span>
              </div>
            </div>
          </div>

          {/* Payment Methods */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Select Payment Method</h3>
            <div className="space-y-3">
              {paymentMethods.map((method) => (
                <div
                  key={method.id}
                  onClick={() => {
                    triggerHapticFeedback('light');
                    setSelectedMethod(method.id);
                  }}
                  className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                    selectedMethod === method.id
                      ? `${method.color} border-current`
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-lg ${
                      selectedMethod === method.id ? 'bg-white' : 'bg-gray-100'
                    }`}>
                      {method.icon}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-800">{method.name}</p>
                      {method.requiresPhone && (
                        <p className="text-xs text-gray-600">Mobile wallet payment</p>
                      )}
                    </div>
                    {selectedMethod === method.id && (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Phone Number Input for Mobile Money */}
          {paymentMethods.find(m => m.id === selectedMethod)?.requiresPhone && (
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {paymentMethods.find(m => m.id === selectedMethod)?.name} Number
              </label>
              <Input
                type="tel"
                placeholder="e.g., 0991234567"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                className="text-lg"
                maxLength={12}
              />
              <p className="text-xs text-gray-500 mt-1">
                You will receive a payment prompt on your phone
              </p>
              {phoneNumber && (() => { try { return localStorage.getItem('yachangu_last_momo') === phoneNumber; } catch { return false; } })() && (
                <p className="text-xs text-emerald-600 mt-1" data-testid="trip-saved-number-hint">Using your saved number — tap Pay to continue</p>
              )}
            </div>
          )}

          {/* Cash Payment Note */}
          {selectedMethod === 'cash' && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-yellow-800">
                💰 Please pay <span className="font-bold">{fareLabel}</span> in cash to your driver
              </p>
            </div>
          )}

          {/* Payment failed — retry or switch provider */}
          {payFailed && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4" data-testid="trip-pay-failed">
              <p className="text-sm font-semibold text-red-800 mb-1">Payment didn't complete</p>
              <p className="text-xs text-red-600 mb-3">{failMessage} You can try again{momoMethods.length > 1 ? ' or pay with a different mobile money provider' : ''}.</p>
              <div className="flex gap-2">
                <Button
                  onClick={() => { setPayFailed(false); handlePayment(); }}
                  data-testid="trip-pay-retry-btn"
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white text-sm h-11"
                >
                  Try again
                </Button>
                {momoMethods.length > 1 && (() => {
                  const idx = momoMethods.findIndex(m => m.id === selectedMethod);
                  const next = momoMethods[(idx + 1) % momoMethods.length];
                  return (
                    <Button
                      onClick={() => { setSelectedMethod(next.id); setPayFailed(false); }}
                      variant="outline"
                      data-testid="trip-pay-switch-btn"
                      className="flex-1 text-sm h-11 border-red-300 text-red-700"
                    >
                      Use {next.name}
                    </Button>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Pay Button */}
          <Button
            onClick={handlePayment}
            disabled={isProcessing}
            data-testid="trip-pay-btn"
            className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg font-semibold"
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                {selectedMethod === 'cash' ? 'Confirm Cash Payment' : payFailed ? `Retry ${fareLabel}` : `Pay ${fareLabel}`}
              </>
            )}
          </Button>
          {isProcessing && statusText && (
            <p className="text-sm text-center text-gray-600 mt-3" data-testid="trip-pay-status">{statusText}</p>
          )}
        </div>
      </Card>
    </div>
  );
};

export default PaymentModal;
